// REFACTORED: 2026-02-02
// FROM src/components/project/ProjectAnalytics.tsx

// ============================================================================
// PROJECT ANALYTICS CHARTS - Reusable chart components for analytics
// ============================================================================

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Tooltip,
  Chip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  PieChart,
  Pie,
  Cell,
  LineChart,
} from "recharts";

import { SEARCE_COLORS } from "../../theme";
import { dashboardApi } from "../../services/api";
import MonthRangePicker from "../MonthRangePicker";
import { formatCurrency } from "../../utils/FormatUtils";
import { truncateAccountName } from "../../utils/FormatUtils";
import { getRAGColor } from "../../utils/colorUtils";
import { getDefaultMonths, isDurationOutOfBounds } from "../../utils/DateUtils";
import { PROJECT_STATUSES, RAG_STATUSES } from "../../constants/ProjectConstants";
import { RegionalViewMode, AccountSortMode, MonthRange } from "../../types/projects";

// ============================================================================
// MODERN REGIONAL PERFORMANCE CHART
// ============================================================================

interface ModernRegionalPerformanceChartProps {
  data: any[];
}

export const ModernRegionalPerformanceChart: React.FC<ModernRegionalPerformanceChartProps> = ({ data }) => {
  const [viewMode, setViewMode] = useState<RegionalViewMode>("combined");

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: SEARCE_COLORS.cardBackground,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 2,
            p: 2,
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: SEARCE_COLORS.textPrimary }}
          >
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  mr: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: SEARCE_COLORS.textPrimary,
                  fontWeight: 600,
                  mr: 1,
                }}
              >
                {entry.name}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: entry.color,
                  fontWeight: 700,
                }}
              >
                {entry.name === "Revenue" || entry.name === "Revenue ($M)"
                  ? formatCurrency(entry.value)
                  : entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: SEARCE_COLORS.textPrimary,
                mb: 0.5,
              }}
            >
              Regional Performance
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}
            >
              Projects & Revenue by Region
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => newValue && setViewMode(newValue)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                borderColor: alpha(SEARCE_COLORS.primary, 0.3),
                color: SEARCE_COLORS.textSecondary,
                fontWeight: 600,
                fontSize: "0.75rem",
                px: 2,
                py: 0.5,
                "&.Mui-selected": {
                  backgroundColor: SEARCE_COLORS.primary,
                  color: SEARCE_COLORS.on_primary,
                  "&:hover": {
                    backgroundColor: SEARCE_COLORS.primary_600,
                  },
                },
                "&:hover": {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                },
              },
            }}
          >
            <ToggleButton value="combined">Combined</ToggleButton>
            <ToggleButton value="projects">Projects</ToggleButton>
            <ToggleButton value="revenue">Revenue</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={400}>
            {viewMode === "combined" ? (
              <ComposedChart
                data={data}
                margin={{ top: 30, right: 30, left: 20, bottom: 30 }}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={SEARCE_COLORS.primary}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={SEARCE_COLORS.primary_600}
                      stopOpacity={0.9}
                    />
                  </linearGradient>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop
                      offset="5%"
                      stopColor={SEARCE_COLORS.success}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={SEARCE_COLORS.success_600}
                      stopOpacity={1}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)}
                  vertical={false}
                />
                <XAxis
                  dataKey="region"
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 600,
                  }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tickMargin={15}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                  tickFormatter={(value) => {
                    // Truncate very long region names for better display
                    return value && value.length > 16
                      ? value.substring(0, 16) + "..."
                      : value;
                  }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 500,
                  }}
                  label={{
                    value: "Projects",
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      textAnchor: "middle",
                      fill: SEARCE_COLORS.textSecondary,
                      fontSize: 12,
                      fontWeight: 600,
                    },
                  }}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 500,
                  }}
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  label={{
                    value: "Revenue ($M)",
                    angle: 90,
                    position: "insideRight",
                    style: {
                      textAnchor: "middle",
                      fill: SEARCE_COLORS.textSecondary,
                      fontSize: 12,
                      fontWeight: 600,
                    },
                  }}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{
                    paddingTop: 20,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="project_count"
                  fill="url(#barGradient)"
                  name="Projects"
                  radius={[6, 6, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_revenue"
                  stroke="url(#lineGradient)"
                  strokeWidth={4}
                  name="Revenue"
                  dot={{
                    fill: SEARCE_COLORS.success,
                    strokeWidth: 3,
                    r: 6,
                    stroke: SEARCE_COLORS.cardBackground,
                  }}
                  activeDot={{
                    fill: SEARCE_COLORS.success_600,
                    strokeWidth: 4,
                    r: 8,
                    stroke: SEARCE_COLORS.cardBackground,
                  }}
                />
              </ComposedChart>
            ) : viewMode === "revenue" ? (
              <BarChart
                data={data}
                margin={{ top: 30, right: 30, left: 20, bottom: 30 }}
              >
                <defs>
                  <linearGradient
                    id="revenueGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={SEARCE_COLORS.success}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={SEARCE_COLORS.success_600}
                      stopOpacity={0.9}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)}
                  vertical={false}
                />
                <XAxis
                  dataKey="region"
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 600,
                  }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tickMargin={15}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                  tickFormatter={(value) => {
                    // Truncate very long region names for better display
                    return value && value.length > 16
                      ? value.substring(0, 16) + "..."
                      : value;
                  }}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 500,
                  }}
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="total_revenue"
                  fill="url(#revenueGradient)"
                  name="Revenue"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            ) : (
              <BarChart
                data={data}
                margin={{ top: 30, right: 30, left: 20, bottom: 30 }}
              >
                <defs>
                  <linearGradient
                    id="projectsGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={SEARCE_COLORS.primary}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={SEARCE_COLORS.primary_600}
                      stopOpacity={0.9}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)}
                  vertical={false}
                />
                <XAxis
                  dataKey="region"
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 600,
                  }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tickMargin={15}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                  tickFormatter={(value) => {
                    // Truncate very long region names for better display
                    return value && value.length > 16
                      ? value.substring(0, 16) + "..."
                      : value;
                  }}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: SEARCE_COLORS.textSecondary,
                    fontWeight: 500,
                  }}
                  axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
                  tickLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="project_count"
                  fill="url(#projectsGradient)"
                  name="Projects"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// COMBINED PROJECT HEALTH MATRIX
// ============================================================================

/** Keeps narrow RAG segments wide enough to show counts (e.g. "11") in the summary capsule. */
const RAG_CAPSULE_SEGMENT_MIN_PX = 48;

interface CombinedProjectHealthMatrixProps {
  data: any[];
}

export const CombinedProjectHealthMatrix: React.FC<CombinedProjectHealthMatrixProps> = ({ data }) => {
  const statuses = PROJECT_STATUSES;
  const ragStatuses = RAG_STATUSES;

  const getMatrixData = () => {
    const matrix: any = {};
    statuses.forEach((status) => {
      matrix[status] = {};
      ragStatuses.forEach((rag) => {
        const item = data.find((d) => d.status === status && d.rag_status === rag);
        matrix[status][rag] = item || { count: 0, revenue: 0 };
      });
    });
    return matrix;
  };

  const matrixData = getMatrixData();

  const getRagDistribution = () => {
    const ragTotals = { Green: 0, Amber: 0, Red: 0 };
    statuses.forEach((status) => {
      ragStatuses.forEach((rag) => {
        ragTotals[rag as keyof typeof ragTotals] += matrixData[status][rag].count;
      });
    });
    const total = ragTotals.Green + ragTotals.Amber + ragTotals.Red;
    return {
      ragTotals,
      total,
      greenPct: total > 0 ? ((ragTotals.Green / total) * 100).toFixed(1) : "0",
      amberPct: total > 0 ? ((ragTotals.Amber / total) * 100).toFixed(1) : "0",
      redPct: total > 0 ? ((ragTotals.Red / total) * 100).toFixed(1) : "0",
    };
  };

  const { ragTotals, greenPct, amberPct, redPct } = getRagDistribution();

  const totalProjects = data.reduce((sum, d) => sum + (d.count || 0), 0);
  const totalRevenueM = data.reduce((sum, d) => sum + (d.revenue || 0), 0) / 1000000;

  const ragHeaderBg = (rag: string) =>
    rag === "Green" ? SEARCE_COLORS.success : rag === "Amber" ? SEARCE_COLORS.warning : SEARCE_COLORS.error;

  const legendItems = [
    { name: "Green", value: ragTotals.Green, pct: greenPct, color: SEARCE_COLORS.success },
    { name: "Amber", value: ragTotals.Amber, pct: amberPct, color: SEARCE_COLORS.warning },
    { name: "Red", value: ragTotals.Red, pct: redPct, color: SEARCE_COLORS.error },
  ];

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          pt: 2,
          pb: 1.25,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, mb: 0.25, fontSize: "1rem" }}>
            Project Health Matrix
          </Typography>
         
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(72px,1fr) repeat(3, minmax(44px,1fr))",
            gap: 0.5,
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, fontSize: "0.65rem" }}>
            Status
          </Typography>
          {ragStatuses.map((rag) => (
            <Chip
              key={rag}
              label={rag}
              size="small"
              sx={{
                backgroundColor: ragHeaderBg(rag),
                color: SEARCE_COLORS.white,
                fontWeight: 700,
                height: 22,
                minWidth: 0,
                "& .MuiChip-label": { px: 0.5, fontSize: "0.65rem", fontWeight: 700 },
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.25 }}>
          {statuses.map((status) => (
            <Box
              key={status}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(72px,1fr) repeat(3, minmax(44px,1fr))",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, fontSize: "0.65rem", lineHeight: 1.2 }}
              >
                {status}
              </Typography>
              {ragStatuses.map((rag) => {
                const cellData = matrixData[status][rag];
                return (
                  <Tooltip
                    key={rag}
                    title={`${cellData.count} projects | $${(cellData.revenue / 1000000).toFixed(1)}M revenue`}
                    arrow
                  >
                    <Box
                      sx={{
                        minHeight: 26,
                        backgroundColor: alpha(
                          rag === "Green"
                            ? SEARCE_COLORS.success
                            : rag === "Amber"
                              ? SEARCE_COLORS.warning
                              : SEARCE_COLORS.error,
                          0.12
                        ),
                        border: `1px solid ${alpha(
                          rag === "Green"
                            ? SEARCE_COLORS.success
                            : rag === "Amber"
                              ? SEARCE_COLORS.warning
                              : SEARCE_COLORS.error,
                          0.35
                        )}`,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, fontSize: "0.7rem" }}>
                        {cellData.count}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            height : 56,
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            borderTop: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.35)}`,
            borderBottom: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.35)}`,
            mb: 0,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 600, fontSize: "0.7rem" }}>
            Total Projects:{" "}
            <Box component="span" sx={{ color: SEARCE_COLORS.textPrimary, fontWeight: 700 }}>
              {totalProjects.toLocaleString()}
            </Box>
          </Typography>
          <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 600, fontSize: "0.7rem" }}>
            Total Revenue:{" "}
            <Box component="span" sx={{ color: SEARCE_COLORS.textPrimary, fontWeight: 700 }}>
              ${totalRevenueM.toFixed(1)}M
            </Box>
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 8 }} />
          {(() => {
            const ragTotal = ragTotals.Green + ragTotals.Amber + ragTotals.Red;
            const jointSegs = [
              { name: "Green" as const, count: ragTotals.Green, pct: greenPct, color: SEARCE_COLORS.success },
              { name: "Amber" as const, count: ragTotals.Amber, pct: amberPct, color: SEARCE_COLORS.warning },
              { name: "Red" as const, count: ragTotals.Red, pct: redPct, color: SEARCE_COLORS.error },
            ].filter((s) => s.count > 0);
            if (ragTotal <= 0 || jointSegs.length === 0) {
              return (
                <Box
                  sx={{
                    borderRadius: 999,
                    py: 1.25,
                    px: 2,
                    flexShrink: 0,
                    backgroundColor: alpha(SEARCE_COLORS.neutral_300, 0.35),
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 600 }}>
                    No RAG distribution
                  </Typography>
                </Box>
              );
            }
            return (
              <Box
                sx={{
                  display: "grid",
                  width: "100%",
                  flexShrink: 0,
                  minHeight: 40,
                  borderRadius: 999,
                  overflow: "hidden",
                  boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.neutral_900, 0.1)}`,
                  gridTemplateColumns: jointSegs
                    .map((s) => `minmax(${RAG_CAPSULE_SEGMENT_MIN_PX}px, ${Math.max(s.count, 1)}fr)`)
                    .join(" "),
                }}
              >
                {jointSegs.map((s) => (
                  <Box
                    key={s.name}
                    sx={{
                      backgroundColor: s.color,
                      color: SEARCE_COLORS.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 1,
                      px: 1,
                      minWidth: RAG_CAPSULE_SEGMENT_MIN_PX,
                      minHeight: 40,
                      flexShrink: 0,
                      boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.15)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: s.name === "Green" ? { xs: "0.68rem", sm: "0.78rem" } : "0.75rem",
                        textAlign: "center",
                        lineHeight: 1.2,
                        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {s.name === "Green"
                        ? `${s.count.toLocaleString()}`
                        : s.count.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })()}
          <Box sx={{ flex: 1, minHeight: 8 }} />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              justifyContent: "center",
              alignItems: "center",
              pt: 0,
              pb: 0,
              flexShrink: 0,
            }}
          >
            {legendItems.map((item) => (
              <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 600, fontSize: "0.68rem" }}>
                  <Box component="span" sx={{ color: SEARCE_COLORS.textPrimary, fontWeight: 700 }}>
                    {item.name}
                  </Box>
                  {" · "}
                  {item.value.toLocaleString()} ({item.pct}%)
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MODERN REVENUE ANALYSIS CHART
// ============================================================================

interface ModernRevenueAnalysisChartProps {
  data: any[];
}

export const ModernRevenueAnalysisChart: React.FC<ModernRevenueAnalysisChartProps> = ({ data }) => {
  // Filter out any data with null or undefined status
  const validData = data.filter(
    (item) =>
      item.status && item.status !== "null" && item.status !== "undefined"
  );

  const totalRevenue = validData.reduce(
    (sum, item) => sum + (item.total_revenue || 0),
    0
  );
  const totalRecognized = validData.reduce(
    (sum, item) => sum + (item.recognized || 0),
    0
  );
  const recognitionRate =
    totalRevenue > 0 ? (totalRecognized / totalRevenue) * 100 : 0;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: SEARCE_COLORS.cardBackground,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 2,
            p: 2,
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: SEARCE_COLORS.textPrimary }}
          >
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  mr: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: SEARCE_COLORS.textPrimary,
                  fontWeight: 600,
                  mr: 1,
                }}
              >
                {entry.name}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: entry.color,
                  fontWeight: 700,
                }}
              >
                {formatCurrency(entry.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: SEARCE_COLORS.textPrimary,
                mb: 0.5,
              }}
            >
              Revenue Analysis
            </Typography>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={validData}>
            <defs>
              <linearGradient
                id="recognizedGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={SEARCE_COLORS.success}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={SEARCE_COLORS.success}
                  stopOpacity={0.2}
                />
              </linearGradient>
              <linearGradient
                id="unrecognizedGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={SEARCE_COLORS.warning}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={SEARCE_COLORS.warning}
                  stopOpacity={0.2}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)}
              vertical={false}
            />
            <XAxis
              dataKey="status"
              tick={{
                fontSize: 11,
                fill: SEARCE_COLORS.textSecondary,
                fontWeight: 600,
              }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tickMargin={15}
              axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
              tickLine={false}
            />
            <YAxis
              tick={{
                fontSize: 11,
                fill: SEARCE_COLORS.textSecondary,
                fontWeight: 500,
              }}
              tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
              axisLine={{ stroke: alpha(SEARCE_COLORS.neutral_300, 0.5) }}
              tickLine={false}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: 20,
                fontSize: 12,
                fontWeight: 600,
              }}
            />
            <Area
              type="monotone"
              dataKey="recognized"
              stackId="1"
              stroke={SEARCE_COLORS.success}
              fill="url(#recognizedGradient)"
              name="Recognized"
            />
            <Area
              type="monotone"
              dataKey="unrecognized"
              stackId="1"
              stroke={SEARCE_COLORS.warning}
              fill="url(#unrecognizedGradient)"
              name="Unrecognized"
            />
            <Line
              type="monotone"
              dataKey="total_revenue"
              stroke={SEARCE_COLORS.primary}
              strokeWidth={3}
              name="Total Revenue"
              dot={{
                fill: SEARCE_COLORS.primary,
                strokeWidth: 3,
                r: 6,
                stroke: SEARCE_COLORS.cardBackground,
              }}
              activeDot={{
                fill: SEARCE_COLORS.primary_600,
                strokeWidth: 4,
                r: 8,
                stroke: SEARCE_COLORS.cardBackground,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: SEARCE_COLORS.success,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}
              >
                Recognized: {formatCurrency(totalRecognized)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: SEARCE_COLORS.warning,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: SEARCE_COLORS.textSecondary, fontWeight: 500 }}
              >
                Unrecognized: {formatCurrency(totalRevenue - totalRecognized)}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: SEARCE_COLORS.primary, fontWeight: 700 }}
          >
            Recognition Rate: {recognitionRate.toFixed(1)}%
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Continue in next file due to length...

